import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';

const DatePicker = ({ date, onDateChange, placeholder, isDateUnavailable, minDate, rangeStart, rangeEnd, hideLabel = false, compact = false }) => {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('search.addDates');
  const isDisabled = (checkDate) => {
    // Disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (checkDate < today) return true;
    
    // Disable dates before minDate (for checkout)
    if (minDate) {
      const minDateTime = new Date(minDate);
      minDateTime.setHours(0, 0, 0, 0);
      if (checkDate <= minDateTime) return true;
    }
    
    // Disable unavailable dates (confirmed bookings & blocked dates)
    if (isDateUnavailable && isDateUnavailable(checkDate)) {
      return true;
    }
    
    return false;
  };

  // Range modifier — highlights all dates strictly between the selected
  // check-in and check-out so the user can see the span at a glance.
  const isInRange = (d) => {
    if (!rangeStart || !rangeEnd) return false;
    const start = new Date(rangeStart); start.setHours(0, 0, 0, 0);
    const end = new Date(rangeEnd); end.setHours(0, 0, 0, 0);
    const cur = new Date(d); cur.setHours(0, 0, 0, 0);
    return cur > start && cur < end;
  };
  const isRangeStart = (d) => {
    if (!rangeStart) return false;
    const a = new Date(d); a.setHours(0, 0, 0, 0);
    const b = new Date(rangeStart); b.setHours(0, 0, 0, 0);
    return a.getTime() === b.getTime();
  };
  const isRangeEnd = (d) => {
    if (!rangeEnd) return false;
    const a = new Date(d); a.setHours(0, 0, 0, 0);
    const b = new Date(rangeEnd); b.setHours(0, 0, 0, 0);
    return a.getTime() === b.getTime();
  };

  // Two visual modes:
  //  - default (search bar pill): flex-1 px-6 py-3 rounded-full — matches the
  //    homepage / search bar layout where many DatePickers sit side-by-side.
  //  - compact (inline cell, e.g., property page BookingWidget): full-width,
  //    smaller padding, square corners — fits inside a bordered grid cell
  //    without bleeding outside the parent's overflow-hidden clip.
  const triggerClass = compact
    ? 'w-full block text-left px-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500'
    : 'flex-1 px-6 py-3 cursor-pointer hover:bg-gray-50 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500';

  return (
    // `modal={false}` is critical: this DatePicker is often rendered inside
    // a Radix Sheet/Dialog (mobile booking sheet, request-to-book modal,
    // etc). Radix Popover's default modal mode installs its own focus trap
    // and body pointer-events: none, which fights the parent Dialog's focus
    // trap on iOS Safari — symptom is the calendar opening and closing
    // immediately, or rendering invisibly behind the sheet overlay.
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClass}
          data-testid="datepicker-trigger"
        >
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-gray-600 shrink-0" />
            <div className="min-w-0">
              {!hideLabel && (
                <label className="text-xs font-semibold text-gray-800 block mb-1 pointer-events-none">
                  {defaultPlaceholder}
                </label>
              )}
              <p className="text-sm text-gray-900 truncate">
                {date ? format(date, 'MMM dd') : t('search.addDates')}
              </p>
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 z-[200]"
        align="start"
        collisionPadding={12}
        sideOffset={6}
        // Avoid the auto-refocus-to-trigger Radix does on close — on iOS
        // that focus pop can scroll the page abruptly inside the mobile
        // booking Sheet.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <style>{`
          /* Booked / unavailable dates — no background fill, just a thin red
             dash through the date number so they read as "taken" subtly. */
          .rdp button[disabled]:not(.rdp-day_selected) {
            cursor: not-allowed !important;
            color: #6B7280 !important;
            background-color: transparent !important;
            opacity: 1 !important;
            font-weight: 400;
            border-radius: 6px;
            text-decoration: none !important;
            position: relative;
          }
          .rdp button[disabled]:not(.rdp-day_selected)::after {
            content: '';
            position: absolute;
            left: 18%;
            right: 18%;
            top: 50%;
            height: 1.5px;
            background-color: #DC2626;
            transform: translateY(-50%);
            pointer-events: none;
            border-radius: 1px;
          }

          /* Range highlight — dark grey block for dates strictly between
             the selected check-in and check-out. */
          .rdp button.rdp-in-range:not([disabled]):not(.rdp-day_selected) {
            background-color: #4B5563 !important;
            color: #FFFFFF !important;
            border-radius: 0 !important;
            font-weight: 500;
          }
          .rdp button.rdp-in-range:not([disabled]):hover {
            background-color: #374151 !important;
          }
          /* Round the start/end edges of the range pill */
          .rdp button.rdp-range-start:not([disabled]) {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
          .rdp button.rdp-range-end:not([disabled]) {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
          }
        `}</style>
        <Calendar
          mode="single"
          selected={date}
          onSelect={(selectedDate) => {
            // Extra safety: prevent selecting disabled dates
            if (selectedDate && !isDisabled(selectedDate)) {
              onDateChange(selectedDate);
            }
          }}
          disabled={isDisabled}
          modifiers={{
            inRange: isInRange,
            rangeStart: isRangeStart,
            rangeEnd: isRangeEnd,
          }}
          modifiersClassNames={{
            inRange: 'rdp-in-range',
            rangeStart: 'rdp-range-start',
            rangeEnd: 'rdp-range-end',
          }}
          initialFocus
          className="rounded-md border"
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
